// DailyChampionsModal.js
// Web port of the DailyWinnersModal in LeaderboardView.swift — the daily/monthly
// champions modal opened by the trophy button on the Leaderboard screen.
//
//   • A DAILY ⇄ MONTHLY toggle (chevrons around the title), mirroring iOS.
//   • Date / month navigation with arrows, disabled at the data bounds. The
//     earliest selectable day is the finalizeRanks launch date (2026-04-23);
//     the latest is yesterday (daily) / last completed month (monthly).
//   • A DRAFTS / STREAKS game-mode toggle. (iOS shows all five modes; only these
//     two are live on web for now — Roster Recon / Gauntlet / Stat Attack will be
//     added when those modes ship on web. The data layer already reads them.)
//   • One row per sport: angled slash panel + a trophy-fill icon to the left of
//     the winner's name + score pill, or "No data available".
//
// All date math is done on the Pacific date KEY string (YYYY-MM-DD / YYYY-MM) so
// the document IDs match the Cloud Function regardless of the browser timezone —
// the same discipline as managers/pacificDate.js.

import React, { useState, useEffect, useCallback } from "react";
import wordmark from "../assets/Rosterable.png";
import trophyFill from "../assets/trophy-fill.svg";
import footballIcon from "../assets/football.svg";
import baseballIcon from "../assets/baseball-fill.svg";
import basketballIcon from "../assets/dribbble-logo-fill.svg";
import hockeyIcon from "../assets/hockey-fill.svg";
import soccerIcon from "../assets/soccer-ball-fill.svg";
import { pacificDateString } from "../managers/pacificDate";
import { loadDailyWinners, loadMonthlyWinners } from "../managers/leaderboardService";

// `name` is the raw sport value used as the Firestore map key (e.g. "Football").
const SPORTS = [
  { id: "football", name: "Football", hex: "#00b04f", icon: footballIcon },
  { id: "baseball", name: "Baseball", hex: "#db2626", icon: baseballIcon },
  { id: "basketball", name: "Basketball", hex: "#ff8c00", icon: basketballIcon },
  { id: "hockey", name: "Hockey", hex: "#0da6e8", icon: hockeyIcon },
  { id: "soccer", name: "Soccer", hex: "#1a66e6", icon: soccerIcon },
];

const GAME_MODES = [
  { id: "draft", label: "DRAFTS" },
  { id: "streak", label: "STREAKS" },
];

// finalizeRanks launch bounds — no winners documents exist before these.
const EARLIEST_DATE = "2026-04-23"; // matches DailyWinnersModal.earliestDate
const EARLIEST_MONTH = "2026-04";

// ─── Timezone-safe key math (operates on YYYY-MM-DD / YYYY-MM strings) ──────────
// We parse the key as a UTC instant and only ever do integer day/month steps in
// UTC, so there's no DST or local-timezone drift — the calendar label round-trips
// exactly. String comparison is valid because both formats are zero-padded.

function addDays(ymd, n) {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

function addMonths(ym, n) {
  const [y, m] = ym.split("-").map(Number);
  const idx = y * 12 + (m - 1) + n;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}`;
}

function monthOf(ymd) {
  return ymd.slice(0, 7);
}

function formatDate(ymd) {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatMonth(ym) {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "long",
    year: "numeric",
  });
}

const clampLow = (value, low) => (value < low ? low : value);

export default function DailyChampionsModal({ onClose }) {
  // Bounds derived from "today" in Pacific time.
  const todayPacific = pacificDateString(new Date());
  const latestDate = clampLow(addDays(todayPacific, -1), EARLIEST_DATE);
  const latestMonth = clampLow(addMonths(monthOf(todayPacific), -1), EARLIEST_MONTH);

  const [mode, setMode] = useState("DAILY"); // "DAILY" | "MONTHLY"
  const [gameMode, setGameMode] = useState("draft"); // "draft" | "streak"
  const [selectedDate, setSelectedDate] = useState(latestDate);
  const [selectedMonth, setSelectedMonth] = useState(latestMonth);
  const [dailyData, setDailyData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch whenever the active key changes.
  useEffect(() => {
    let cancelled = false;
    const isDaily = mode === "DAILY";
    setLoading(true);
    const fetch = isDaily
      ? loadDailyWinners(selectedDate)
      : loadMonthlyWinners(selectedMonth);
    fetch
      .then((data) => {
        if (cancelled) return;
        if (isDaily) setDailyData(data);
        else setMonthlyData(data);
      })
      .catch(() => {
        if (cancelled) return;
        if (isDaily) setDailyData(null);
        else setMonthlyData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, selectedDate, selectedMonth]);

  const toggleMode = useCallback(() => {
    setMode((m) => (m === "DAILY" ? "MONTHLY" : "DAILY"));
  }, []);

  // Navigation + bounds for the active mode.
  const isDaily = mode === "DAILY";
  const canGoBack = isDaily
    ? selectedDate > EARLIEST_DATE
    : selectedMonth > EARLIEST_MONTH;
  const canGoForward = isDaily
    ? selectedDate < latestDate
    : selectedMonth < latestMonth;

  function navigate(delta) {
    if (isDaily) {
      const next = addDays(selectedDate, delta);
      if (next < EARLIEST_DATE || next > latestDate) return;
      setSelectedDate(next);
    } else {
      const next = addMonths(selectedMonth, delta);
      if (next < EARLIEST_MONTH || next > latestMonth) return;
      setSelectedMonth(next);
    }
  }

  const activeData = isDaily ? dailyData : monthlyData;
  const dateLabel = isDaily ? formatDate(selectedDate) : formatMonth(selectedMonth);

  function entryFor(sport) {
    const map = activeData ? activeData[gameMode] : null;
    return map ? map[sport.name] || null : null;
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        {/* Header — centered wordmark + close */}
        <div style={styles.header}>
          <img src={wordmark} alt="Rosterable" style={styles.wordmark} />
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* Title with daily/monthly chevron toggle */}
        <div style={styles.titleRow}>
          <button style={styles.titleChev} onClick={toggleMode} aria-label="Switch champions mode">
            ‹
          </button>
          <span style={styles.title}>
            {isDaily ? "DAILY CHAMPIONS" : "MONTHLY CHAMPIONS"}
          </span>
          <button style={styles.titleChev} onClick={toggleMode} aria-label="Switch champions mode">
            ›
          </button>
        </div>

        {/* Date / month navigation */}
        <div style={styles.picker}>
          <button
            style={{ ...styles.chev, opacity: canGoBack ? 1 : 0.2, cursor: canGoBack ? "pointer" : "default" }}
            onClick={() => navigate(-1)}
            disabled={!canGoBack}
          >
            ‹
          </button>
          <span style={styles.pickerLabel}>{dateLabel}</span>
          <button
            style={{ ...styles.chev, opacity: canGoForward ? 1 : 0.2, cursor: canGoForward ? "pointer" : "default" }}
            onClick={() => navigate(1)}
            disabled={!canGoForward}
          >
            ›
          </button>
        </div>

        {/* Game-mode toggle */}
        <div style={styles.modeRow}>
          {GAME_MODES.map((gm) => {
            const selected = gm.id === gameMode;
            return (
              <button
                key={gm.id}
                onClick={() => setGameMode(gm.id)}
                style={{
                  ...styles.modeBtn,
                  background: selected ? "rgba(124,106,247,0.18)" : "rgba(255,255,255,0.05)",
                  border: selected ? "1px solid rgba(124,106,247,0.6)" : "1px solid rgba(255,255,255,0.08)",
                  color: selected ? "#fff" : "rgba(255,255,255,0.5)",
                }}
              >
                {gm.label}
              </button>
            );
          })}
        </div>

        {/* Winner rows */}
        <div style={styles.rows}>
          {SPORTS.map((sport) => (
            <WinnerRow
              key={sport.id}
              sport={sport}
              entry={entryFor(sport)}
              loading={loading && !activeData}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function WinnerRow({ sport, entry, loading }) {
  return (
    <div
      style={{
        ...styles.row,
        border: `1px solid ${entry ? `${sport.hex}4d` : "rgba(255,255,255,0.06)"}`,
      }}
    >
      {/* Angled sport panel */}
      <div style={{ ...styles.slash, background: sport.hex }}>
        <span
          style={{
            ...styles.slashIcon,
            WebkitMaskImage: `url(${sport.icon})`,
            maskImage: `url(${sport.icon})`,
          }}
        />
      </div>

      {entry ? (
        <>
          <div style={styles.rowText}>
            <div style={styles.nameLine}>
              {/* trophy-fill to the left of the champion's name */}
              <span
                style={{
                  ...styles.trophyGlyph,
                  background: sport.hex,
                  WebkitMaskImage: `url(${trophyFill})`,
                  maskImage: `url(${trophyFill})`,
                }}
              />
              <span style={styles.winnerName}>{entry.username}</span>
            </div>
            <span style={styles.sportName}>{sport.name}</span>
          </div>
          <div style={{ ...styles.scorePill, background: `${sport.hex}1f`, color: sport.hex }}>
            {entry.score}
          </div>
        </>
      ) : (
        <div style={styles.rowText}>
          <span style={styles.statusName}>{loading ? "Loading…" : "No data available"}</span>
          <span style={styles.sportNameDim}>{sport.name}</span>
        </div>
      )}
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    zIndex: 1000,
  },
  card: {
    width: "100%",
    maxWidth: "420px",
    background: "#1c2130",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "20px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    padding: "16px 20px 10px",
  },
  wordmark: { height: "20px", width: "auto" },
  closeBtn: {
    position: "absolute",
    right: "16px",
    top: "14px",
    width: 30,
    height: 30,
    borderRadius: "50%",
    border: "none",
    background: "rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.6)",
    fontSize: "13px",
    cursor: "pointer",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    paddingBottom: "8px",
  },
  titleChev: {
    background: "none",
    border: "none",
    color: "rgba(255,255,255,0.4)",
    fontSize: "20px",
    lineHeight: 1,
    cursor: "pointer",
    padding: "0 2px",
  },
  title: {
    fontSize: "16px",
    fontWeight: "900",
    color: "#fff",
    letterSpacing: "1.2px",
    textAlign: "center",
    minWidth: "180px",
  },
  picker: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "20px",
    padding: "0 20px 10px",
  },
  chev: { background: "none", border: "none", color: "#fff", fontSize: "24px", lineHeight: 1, padding: "0 4px" },
  pickerLabel: { fontSize: "14px", fontWeight: "700", color: "#fff", letterSpacing: "0.3px", minWidth: "170px", textAlign: "center" },
  modeRow: {
    display: "flex",
    gap: "8px",
    padding: "0 24px 14px",
  },
  modeBtn: {
    flex: 1,
    padding: "9px 0",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "800",
    letterSpacing: "0.8px",
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  rows: { display: "flex", flexDirection: "column", gap: "10px", padding: "0 24px 24px" },
  row: {
    display: "flex",
    alignItems: "center",
    height: "52px",
    background: "#12161f",
    borderRadius: "12px",
    overflow: "hidden",
  },
  slash: {
    width: "48px",
    height: "52px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    clipPath: "polygon(0 0, 100% 0, 60% 100%, 0 100%)",
  },
  slashIcon: {
    width: "22px",
    height: "22px",
    background: "rgba(0,0,0,0.85)",
    transform: "translateX(-3px)",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskPosition: "center",
    maskPosition: "center",
  },
  rowText: { display: "flex", flexDirection: "column", gap: "1px", flex: 1, minWidth: 0, paddingLeft: "10px" },
  nameLine: { display: "flex", alignItems: "center", gap: "6px", minWidth: 0 },
  trophyGlyph: {
    width: 14,
    height: 14,
    flexShrink: 0,
    display: "inline-block",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskPosition: "center",
    maskPosition: "center",
  },
  winnerName: { fontSize: "16px", fontWeight: "700", color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  sportName: { fontSize: "10px", fontWeight: "500", color: "rgba(255,255,255,0.4)", letterSpacing: "0.2px" },
  statusName: { fontSize: "13px", fontWeight: "700", color: "rgba(255,255,255,0.45)", letterSpacing: "0.5px" },
  sportNameDim: { fontSize: "10px", fontWeight: "500", color: "rgba(255,255,255,0.25)", letterSpacing: "0.2px" },
  scorePill: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "64px",
    height: "34px",
    borderRadius: "8px",
    marginRight: "10px",
    flexShrink: 0,
    fontSize: "18px",
    fontWeight: "800",
    padding: "0 8px",
  },
};
